import { Events, pushEvent } from '@ff-os/shared-react/utils/analytics';
import { Area } from '@ff-os/shared-utils/types/Area';
import { selectBorderById } from '@ff-scene-plugins/photo-prints-redux/selectors/borderSelectors';
import { selectSizeById } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectAllVariants } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { actions as groupActions, selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { actions as photoActions, selectPhotosByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { findVariant } from '@ff-scene-plugins/photo-prints-shared/variants';
import { applyFirstMagicBorderIfFotofun } from '../helpers/applyFirstMagicBorderIfFotofun';
import { applyMagicBorderThunk } from './applyMagicBorderThunk';
import { updateWithScheduledReset } from './withScheduledReset';

// Helper function to calculate crop with forced aspect ratio (copied from privateAssetHandling.ts)
const calculateInitialCropWithForcedAspectRatio = (
    imageWidth: number,
    imageHeight: number,
    size?: PhotoPrintsJson.Size,
    hasBorder?: boolean
): Area => {
    // Default crop covers the full image
    let crop: Area = {
        x: 0,
        y: 0,
        width: imageWidth,
        height: imageHeight
    };

    // If the size has a forced aspect ratio and borders are applied, apply it
    if (size && size.borderAspectRatio && hasBorder) {
        const [widthStr, heightStr] = size.borderAspectRatio.split('/');
        const overrideWidth = parseFloat(widthStr);
        const overrideHeight = parseFloat(heightStr);

        if (!isNaN(overrideWidth) && !isNaN(overrideHeight) && overrideHeight !== 0) {
            // Use the exact aspect ratio from border-aspect-ratio
            const targetAspectRatio = overrideWidth / overrideHeight;

            // Calculate available space with crop factor applied
            const availableWidth = imageWidth * 1;
            const availableHeight = imageHeight * 1;

            // Calculate new crop dimensions that fit the specified aspect ratio within the available space
            let newCropWidth, newCropHeight;

            // Try to fit by the dimension that gives us the largest crop within available space
            const widthBasedHeight = availableWidth / targetAspectRatio;
            const heightBasedWidth = availableHeight * targetAspectRatio;

            if (widthBasedHeight <= availableHeight) {
                // Fit by width
                newCropWidth = availableWidth;
                newCropHeight = widthBasedHeight;
            } else {
                // Fit by height
                newCropWidth = heightBasedWidth;
                newCropHeight = availableHeight;
            }

            // Center the crop within the image (this automatically leaves space on all sides)
            const borderX = (imageWidth - newCropWidth) / 2;
            const borderY = (imageHeight - newCropHeight) / 2;

            crop = {
                x: borderX,
                y: borderY,
                width: newCropWidth,
                height: newCropHeight
            };
        }
    }

    return crop;
};

export const updateVariant = (
    paperId: string | undefined,
    sizeId: string | undefined,
    groupId: string,
    borderId: string | undefined,
    sourceGroupId?: string) => {

    return (dispatch: PhotoPrintsPdpDispatch, getState: () => PhotoPrintsPdpState) => {

        const state = getState();
        const variant = findVariant(selectAllVariants(state), {
            paperId,
            sizeId,
            borderId
        });

        if (variant) {

            pushEvent(Events.CHANGE_VARIANT, {
                variantId: variant.id,
                groupId: groupId,
                paperId: paperId,
                sizeId: sizeId,
                borderId: borderId
            });

            dispatch(photoActions.clearReproStrings({ groupId: groupId }));
            dispatch(groupActions.updateVariant({ variantId: variant.id, groupId: groupId }));
            dispatch(updateWithScheduledReset({ id: groupId, key: 'hasVariantUpdated', value: true }));

            // Recalculate photo crops when variant changes affect border aspect ratio requirements
            const updatedState = getState();
            const newSize = selectSizeById(updatedState, variant.sizeId);
            const newBorder = selectBorderById(updatedState, variant.borderId);
            const hasBorder = newBorder?.value ?? false;

            // Get the previous variant to check if it had borderAspectRatio
            const currentGroup = selectGroupById(state, groupId);
            const previousVariant = currentGroup ? selectAllVariants(state).find(v => v.id === currentGroup.variantId) : null;
            const previousSize = previousVariant ? selectSizeById(state, previousVariant.sizeId) : null;
            const previousBorder = previousVariant ? selectBorderById(state, previousVariant.borderId) : null;
            const hadPreviousBorder = previousBorder?.value ?? false;

            if (newSize && previousSize) {
                const photosInGroup = selectPhotosByGroupId(updatedState, groupId)
                    .filter(photo => photo.processed) as PhotoPrintsJson.ProcessedPhoto[];

                for (const photo of photosInGroup) {
                    let newCrop: Area;

                    // If the new size has borderAspectRatio and borders are applied, use forced aspect ratio
                    if (newSize.borderAspectRatio && hasBorder) {
                        newCrop = calculateInitialCropWithForcedAspectRatio(
                            photo.width,
                            photo.height,
                            newSize,
                            hasBorder
                        );
                    } else if (previousSize.borderAspectRatio && hadPreviousBorder) {
                        // Only reset to full image crop when the previous size had borderAspectRatio
                        newCrop = {
                            x: 0,
                            y: 0,
                            width: photo.width,
                            height: photo.height
                        };
                    } else {
                        // No change needed - neither had borderAspectRatio constraints
                        continue;
                    }

                    // Only update crop if it actually changed to avoid unnecessary re-renders
                    const currentCrop = photo.crop;
                    const cropChanged = (
                        Math.abs(newCrop.x - currentCrop.x) > 0.1 ||
                        Math.abs(newCrop.y - currentCrop.y) > 0.1 ||
                        Math.abs(newCrop.width - currentCrop.width) > 0.1 ||
                        Math.abs(newCrop.height - currentCrop.height) > 0.1
                    );

                    if (cropChanged) {
                        dispatch(photoActions.updateCrop({
                            photoId: photo.id,
                            crop: newCrop
                        }));
                    }
                }
            }

            // Check if we should preserve border from source group
            if (sourceGroupId && variant.id.includes('fotofun')) {
                const sourceGroup = selectGroupById(state, sourceGroupId);
                if (sourceGroup && (sourceGroup.magicBorderId || sourceGroup.patternId)) {
                    // Apply the source group's border instead of default
                    const preservedBorderId = sourceGroup.magicBorderId || sourceGroup.patternId;
                    if (preservedBorderId) {
                        dispatch(applyMagicBorderThunk(groupId, preservedBorderId));
                        return; // Skip the default border application
                    }
                }
            }

            // Default behavior for non-configurator flows or when no border to preserve
            dispatch(applyFirstMagicBorderIfFotofun(groupId, variant.id));
        }
    };
};